<template>
<div v-if="walletConnected" class="compass-card mb-4">
    <div class="d-flex justify-content-between align-items-center mb-3">
        <h2 style="font-size: 1.2rem; font-weight: 600; margin: 0;">Your Lending Positions</h2>
        <button class="compass-btn compass-btn--sm compass-btn--outline" @click="fetchPositions" :disabled="loading">
            <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i> Refresh
        </button>
    </div>

    <!-- Loading -->
    <div v-if="loading && !positions.length" class="text-center py-4" style="color: #9ca3af;">
        <span class="spinner-border spinner-border-sm me-1"></span> Loading positions...
    </div>

    <!-- No positions -->
    <div v-else-if="!loading && !positions.length" class="text-center py-4" style="color: #9ca3af;">
        <i class="fas fa-inbox mb-2" style="font-size: 1.5rem;"></i>
        <p class="mb-0">No lending positions found. Supply tokens above to start earning.</p>
    </div>

    <!-- Positions table -->
    <template v-else>
        <!-- Summary stats -->
        <div class="row mb-3">
            <div class="col-4">
                <div class="small" style="color: #6b7280;">Total Supplied</div>
                <div class="font-mono-num" style="font-weight: 600;">${{ formatCompact(totalSupplied) }}</div>
            </div>
            <div class="col-4">
                <div class="small" style="color: #6b7280;">Total Borrowed</div>
                <div class="font-mono-num" style="font-weight: 600;">${{ formatCompact(totalBorrowed) }}</div>
            </div>
            <div class="col-4">
                <div class="small" style="color: #6b7280;">Net APY</div>
                <div class="font-mono-num pct-change--positive" style="font-weight: 600;">{{ netApy }}</div>
            </div>
        </div>

        <div class="table-mobile-scroll">
            <table class="compass-table">
                <thead>
                    <tr>
                        <th>Protocol</th>
                        <th>Token</th>
                        <th>Type</th>
                        <th class="text-end">Amount</th>
                        <th class="text-end">Value</th>
                        <th class="text-end">APY</th>
                        <th class="text-end">Health</th>
                        <th class="text-center">Action</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="pos in positions" :key="pos.id || (pos.protocol + pos.token + pos.type)">
                        <td><strong>{{ capitalize(pos.protocol) }}</strong></td>
                        <td>{{ pos.token }}</td>
                        <td>
                            <span :class="pos.type === 'supply' ? 'compass-badge compass-badge--purple' : 'compass-badge compass-badge--outline'">
                                {{ pos.type === 'supply' ? 'Supplied' : 'Borrowed' }}
                            </span>
                        </td>
                        <td class="td-num text-end">{{ formatAmount(pos.amount) }}</td>
                        <td class="td-num text-end">${{ formatCompact(pos.value_usd) }}</td>
                        <td class="td-num text-end">
                            <span :class="pos.type === 'supply' ? 'pct-change--positive' : ''">
                                {{ formatApy(pos.apy) }}
                            </span>
                        </td>
                        <td class="td-num text-end">
                            <span v-if="pos.health_factor" :style="{ color: healthColor(pos.health_factor) }">
                                {{ Number(pos.health_factor).toFixed(2) }}
                            </span>
                            <span v-else style="color: #9ca3af;">—</span>
                        </td>
                        <td class="text-center">
                            <div v-if="editingPos === pos" class="d-flex flex-column align-items-center gap-1" style="min-width: 140px;">
                                <el-input
                                    v-model="actionAmount"
                                    type="number"
                                    size="small"
                                    :placeholder="'Max: ' + formatAmount(pos.amount)"
                                    style="width: 130px;"
                                />
                                <div class="d-flex gap-1">
                                    <button class="compass-btn compass-btn--sm compass-btn--primary"
                                        @click="performAction(pos)"
                                        :disabled="actionLoading === pos || !actionAmount">
                                        {{ actionLoading === pos ? '...' : 'Confirm' }}
                                    </button>
                                    <button class="compass-btn compass-btn--sm compass-btn--outline"
                                        @click="editingPos = null; actionAmount = ''">
                                        Cancel
                                    </button>
                                </div>
                                <button class="compass-btn compass-btn--sm compass-btn--outline" style="font-size: 0.7rem;"
                                    @click="actionAmount = String(pos.amount)">
                                    Max
                                </button>
                            </div>
                            <button v-else
                                class="compass-btn compass-btn--sm compass-btn--outline"
                                @click="startAction(pos)"
                            >
                                {{ pos.type === 'supply' ? 'Withdraw' : 'Repay' }}
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <div v-if="actionError" class="mt-2 small text-danger">{{ actionError }}</div>
    </template>
</div>
</template>

<script>
import { useSolSdk } from '../../composables/useSolSdk';
import { useLendFormatters } from '../../composables/useLendFormatters';

export default {
    name: 'LendPositions',
    setup() {
        const { getSdk, isReady } = useSolSdk();
        const { capitalize, formatApy, formatCompact, formatAmount } = useLendFormatters();
        return { getSdk, sdkReady: isReady, capitalize, formatApy, formatCompact, formatAmount };
    },
    data() {
        return {
            positions: [],
            loading: false,
            actionLoading: null,
            editingPos: null,
            actionAmount: '',
            actionError: null,
        };
    },
    computed: {
        walletConnected() {
            return !!window.wallet?.publicKey?.value;
        },
        totalSupplied() {
            return this.positions
                .filter(p => p.type === 'supply')
                .reduce((sum, p) => sum + (p.value_usd || 0), 0);
        },
        totalBorrowed() {
            return this.positions
                .filter(p => p.type === 'borrow')
                .reduce((sum, p) => sum + (p.value_usd || 0), 0);
        },
        netApy() {
            const supplyValue = this.totalSupplied;
            const borrowValue = this.totalBorrowed;
            if (!supplyValue && !borrowValue) return '—';

            const supplyEarnings = this.positions
                .filter(p => p.type === 'supply')
                .reduce((sum, p) => sum + ((p.value_usd || 0) * (p.apy || 0) / 100), 0);
            const borrowCost = this.positions
                .filter(p => p.type === 'borrow')
                .reduce((sum, p) => sum + ((p.value_usd || 0) * (p.apy || 0) / 100), 0);

            const totalValue = supplyValue || 1;
            const net = ((supplyEarnings - borrowCost) / totalValue) * 100;
            return net.toFixed(2) + '%';
        },
    },
    watch: {
        walletConnected(connected) {
            if (connected) this.fetchPositions();
        },
    },
    mounted() {
        if (this.walletConnected) {
            this.fetchPositions();
        }
    },
    methods: {
        async fetchPositions() {
            if (!this.walletConnected) return;
            this.loading = true;
            try {
                const sdk = await this.getSdk();
                const walletAddress = window.wallet.publicKey.value.toBase58();
                const result = await sdk.lend.getPositions(walletAddress);
                // SDK returns array of { protocol, token, mint, type, amount, valueUsd, apy, healthFactor }
                this.positions = (result || []).map(p => ({
                    protocol: p.protocol,
                    token: p.token,
                    mint: p.mint,
                    type: p.type === 'deposit' ? 'supply' : p.type,
                    amount: p.amount,
                    value_usd: p.valueUsd || p.value_usd || 0,
                    apy: p.apy ? p.apy * 100 : 0,
                    health_factor: p.healthFactor || p.health_factor || null,
                }));
            } catch (e) {
                console.error('[LendPositions] Failed to fetch:', e);
            } finally {
                this.loading = false;
            }
        },
        // capitalize, formatApy, formatAmount, formatCompact provided by useLendFormatters via setup()
        healthColor(factor) {
            if (factor >= 2) return 'var(--compass-success, #22c55e)';
            if (factor >= 1.5) return '#f59e0b';
            return 'var(--compass-danger, #ef4444)';
        },
        startAction(pos) {
            this.editingPos = pos;
            this.actionAmount = String(pos.amount);
            this.actionError = null;
        },
        async performAction(pos) {
            const amount = parseFloat(this.actionAmount);
            if (!amount || amount <= 0) return;
            this.actionLoading = pos;
            this.actionError = null;
            try {
                const sdk = await this.getSdk();
                if (pos.type === 'supply') {
                    await sdk.lend.withdraw('default', pos.token, amount, pos.protocol);
                } else {
                    await sdk.lend.repay('default', pos.token, amount, pos.protocol);
                }
                this.editingPos = null;
                this.actionAmount = '';
                await this.fetchPositions();
            } catch (e) {
                console.error('[LendPositions] Action failed:', e);
                this.actionError = 'Transaction failed: ' + (e.message || 'Unknown error');
            } finally {
                this.actionLoading = null;
            }
        },
    },
};
</script>
